import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-hook-inview';
import Hls from 'hls.js';

const ShowVideo = props => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [inView, setInView] = useState(true);

  const [containerRef] = useInView({
    threshold: 0.3,
    onEnter: () => setInView(true),
    onLeave: () => setInView(false)
  });

  const poster = props.thumbnail || props.dataMemory?.thumbnail;

  // The primary source is the transcoded, optimized stream from AWS MediaConvert (url / srcVideo).
  // The raw upload (urlOrigin) is kept as a fallback only.
  const primarySrc = props.srcVideo || props.dataMemory?.url;
  const fallbackSrc = props.dataMemory?.urlOrigin;

  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // If unmuted autoplay is blocked by browser policy, fall back to muted autoplay
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }, []);

  // Initialize video playback using HLS or direct native source
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !primarySrc) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHls = primarySrc.includes('.m3u8');

    if (isHls) {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari / iOS)
        video.src = primarySrc;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90
        });
        hlsRef.current = hls;
        hls.loadSource(primarySrc);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (inView && !props.pauseVideo) {
            attemptPlay();
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                if (fallbackSrc && video.src !== fallbackSrc) {
                  hls.destroy();
                  hlsRef.current = null;
                  video.src = fallbackSrc;
                  attemptPlay();
                }
                break;
            }
          }
        });
      } else if (fallbackSrc) {
        video.src = fallbackSrc;
      } else {
        video.src = primarySrc;
      }
    } else {
      video.src = primarySrc;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [
    primarySrc,
    fallbackSrc,
    inView,
    props.pauseVideo,
    attemptPlay
  ]);

  // Handle play / pause based on in-view status and pauseVideo prop
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (inView && !props.pauseVideo) {
      attemptPlay();
    } else {
      video.pause();
    }
  }, [inView, props.pauseVideo, attemptPlay]);

  const handleCanPlay = () => {
    if (inView && !props.pauseVideo) {
      attemptPlay();
    }
  };

  const handlePlay = () => {
    if (props.memoryPlayingHandler && props.memoryId) {
      props.memoryPlayingHandler(props.memoryId);
    }
  };

  const handleError = () => {
    const video = videoRef.current;
    if (video && fallbackSrc && video.src !== fallbackSrc) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.src = fallbackSrc;
      attemptPlay();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        poster={poster}
        onCanPlay={handleCanPlay}
        onPlay={handlePlay}
        onError={handleError}
        style={{
          width: '100%',
          height: '100%',
          maxHeight: props.isFullScreen ? '100%' : '480px',
          objectFit: 'contain',
          backgroundColor: '#000000',
          display: 'block'
        }}
      />
    </div>
  );
};

ShowVideo.propTypes = {
  // eslint-disable-next-line react/require-default-props
  isFullScreen: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  srcVideo: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  dataMemory: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  memoryView: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  memoryId: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  memory: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  memoryPlayingHandler: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  memoryPlaying: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  pauseVideo: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  thumbnail: PropTypes.string
};

export default ShowVideo;
